import { request } from '@helpers/fetch';
import { Cart } from '@models/cart';
import { Response } from '@models/response';
import {
  UserRegisterPayload,
  UserLoginPayload,
  ResetPasswordPayload,
  SetPasswordPayload,
  UserLoginResponse,
  SessionInfoResponse,
  UserRegisterResponse,
  ResetPasswordResponse,
  SetPasswordResponse,
  ProfileResponse,
  ProfilePayload,
  ProfilePaymentMethod,
  GetPaymentMethodsResponse,
  UserUpdateMetaPayload,
  VerifyEmailResponse,
  UserLoginTOTPResponse,
  UserTOTPVerifyResponse,
  AuthMethod,
} from '@models/user';

export const apiUser = {
  sessionInfo(): Promise<Response<SessionInfoResponse>> {
    return request.post('/api/v1/users/session-info');
  },

  login(payload: UserLoginPayload): Promise<UserLoginResponse | UserLoginTOTPResponse> {
    return request.post('/actions/users/login', payload);
  },

  verifyTOTP(payload: { code: string }): Promise<Response<UserTOTPVerifyResponse>> {
    return request.post('/actions/auth/verify-totp', payload);
  },

  getAuthMethods(): Promise<Response<AuthMethod[]>> {
    return request.get('/api/v1/users/get-auth-methods');
  },

  getTOTPSecret(): Promise<Response<{ secret: string, qr: string }>> {
    return request.get('/api/v1/users/get-totp-secret');
  },

  sendEmailVerification(
    email: string,
    siteHandle?: string,
    firstName?: string,
  ): Promise<Response<boolean>> {
    return request.post('/api/v1/users/send-email-verification', {
      email,
      ...(siteHandle ? { siteHandle } : {}),
      ...(firstName ? { firstName } : {}),
    });
  },

  register(payload: UserRegisterPayload): Promise<Response<UserRegisterResponse>> {
    return request.post('/api/v1/users/register', payload);
  },

  resetPassword(payload: ResetPasswordPayload): Promise<ResetPasswordResponse> {
    return request.post('/api/v1/users/send-password-reset', {
      email: payload.loginName.trim(),
    });
  },

  setPassword(payload: SetPasswordPayload): Promise<SetPasswordResponse> {
    return request.post('/actions/users/set-password', payload);
  },

  uploadPhoto(payload: FormData): Promise<Response<string>> {
    return request.postForm('/api/v1/document/photo', payload);
  },

  uploadDocument(payload: FormData): Promise<Response<string>> {
    return request.postForm('/api/v1/document/document/document', payload);
  },

  uploadInsuranceFront(payload: FormData): Promise<Response<string>> {
    return request.postForm('/api/v1/document/document/insuranceCardFront', payload);
  },

  uploadInsuranceBack(payload: FormData): Promise<Response<string>> {
    return request.postForm('/api/v1/document/document/insuranceCardBack', payload);
  },

  uploadIDPicture(payload: FormData): Promise<Response<string>> {
    return request.postForm('/api/v1/document/document/idPicture', payload);
  },

  deletePhoto(): Promise<Response<string>> {
    return request.remove('/api/v1/document/photo');
  },

  deleteDocument(): Promise<Response<string>> {
    return request.remove('/api/v1/document/document/document');
  },

  deleteInsuranceFront(): Promise<Response<string>> {
    return request.remove('/api/v1/document/document/insuranceCardFront');
  },

  deleteInsuranceBack(): Promise<Response<string>> {
    return request.remove('/api/v1/document/document/insuranceCardBack');
  },

  deleteIDPicture(): Promise<Response<string>> {
    return request.remove('/api/v1/document/document/idPicture');
  },

  getProfile(): Promise<Response<ProfileResponse>> {
    return request.get('/api/v1/profile');
  },

  updateProfile(payload: ProfilePayload): Promise<Response<ProfileResponse>> {
    return request.put('/api/v1/profile', payload);
  },

  updateMeta(payload: UserUpdateMetaPayload): Promise<Response<boolean>> {
    const data = new FormData();
    if (payload.fbc) {
      data.set('fbc', payload.fbc);
    }
    if (payload.fbp) {
      data.set('fbp', payload.fbp);
    }
    if (payload.campaign) {
      data.set('campaign', payload.campaign);
    }
    if (payload.efTransactionId) {
      data.set('efTransactionId', payload.efTransactionId);
    }
    if (payload.utmSource) {
      data.set('utmSource', payload.utmSource);
    }
    if (payload.utmMedium) {
      data.set('utmMedium', payload.utmMedium);
    }
    if (payload.marketingConsent !== undefined && payload.marketingConsent !== null) {
      data.set('marketingConsent', payload.marketingConsent ? 'true' : 'false');
    }

    return request.postForm('/api/v1/patient/update-meta', data);
  },

  getPaymentMethod(): Promise<Response<ProfilePaymentMethod>> {
    return request.get('/api/v1/profile/payment-method');
  },

  removePaymentMethod(): Promise<Response<[]>> {
    return request.remove('/api/v1/profile/payment-method');
  },

  getPaymentMethods(): Promise<Response<GetPaymentMethodsResponse>> {
    return request.get('/api/v2/profile/payment-methods');
  },

  deletePaymentMethod(payload: { paymentSourceId: string }): Promise<Response<[]>> {
    return request.remove('/api/v2/profile/payment-method', payload);
  },

  getPortalSession(): Promise<Response<any>> {
    return request.get('/api/v1/profile/portal-session');
  },

  getCartByCheckoutId(checkoutId: string): Promise<Response<Cart>> {
    return request.get(`/api/v1/cart/checkout/${checkoutId}`);
  },

  getProfessions(): Promise<Response<any>> {
    return request.get('/api/v1/users/professions');
  },

  chargebeeSync(): Promise<any> {
    return request.put('/api/v1/profile/chargebee-sync');
  },

  verifyEmail(email: string): Promise<Response<VerifyEmailResponse>> {
    return request.post('/api/v1/users/verify-email', { email });
  },
};
